import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { dxcBelow } from '../media/dxc-media';

export type DxcCrumb = { label: string; link?: string };

/**
 * Encabezado de vista. Toda vista empieza igual: migas opcionales, título, una
 * línea que explica qué se hace ahí, y las acciones a la derecha.
 *
 * La descripción no es opcional por capricho: es el único lugar donde el
 * producto puede explicar un concepto fiscal sin abrir un modal.
 *
 * En un teléfono (`sm`) la acción principal toma la fila y las secundarias que
 * la vista marca con `data-dxc-collapse` se retiran; lo que la vista proyecte
 * en `[more]` —un botón `--icon` con su `mat-menu` de `.dxc-menu-item`— aparece
 * entonces al final de la fila. Fuera de `sm` el hueco `[more]` no se monta: el
 * encabezado de escritorio es exactamente el de siempre.
 *
 * @example
 * <dxc-page-header title="Operadores" description="…">
 *   <button type="button" class="dxc-btn dxc-btn--secondary" data-dxc-collapse (click)="import()">Importar</button>
 *   <button type="button" class="dxc-btn dxc-btn--primary" (click)="create()">Agregar operador</button>
 *   <button more type="button" class="dxc-btn dxc-btn--ghost dxc-btn--icon" aria-label="Más acciones"
 *     [matMenuTriggerFor]="moreMenu"><dxc-icon name="more-vertical"></dxc-icon></button>
 *   <mat-menu #moreMenu><button mat-menu-item class="dxc-menu-item" (click)="import()">Importar</button></mat-menu>
 * </dxc-page-header>
 */
@Component({
    standalone: true,
    selector: 'dxc-page-header',
    template: `
        <div class="dxc-page-head">
            <div style="display:flex;align-items:flex-start;min-width:0">
                @if (showBack) {
                    <button type="button" class="dxc-page-head__back" aria-label="Volver" (click)="back.emit()">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5M11 6l-6 6 6 6"/></svg>
                    </button>
                }
                <div style="min-width:0">
                    @if (breadcrumb.length) {
                        <nav class="dxc-breadcrumb">
                            @for (crumb of breadcrumb; track crumb.label; let last = $last) {
                                @if (last) { <span>{{ crumb.label }}</span> }
                                @else {
                                    <a [attr.href]="crumb.link || null">{{ crumb.label }}</a>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="opacity:.6"><path d="m9 18 6-6-6-6"/></svg>
                                }
                            }
                        </nav>
                    }
                    <h1>{{ title }}</h1>
                    @if (description) { <p>{{ description }}</p> }
                </div>
            </div>
            <div class="dxc-page-head__actions">
                <ng-content></ng-content>
                @if (phone()) { <span class="dxc-page-head__more"><ng-content select="[more]"></ng-content></span> }
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DxcPageHeaderComponent {
    /** Sustantivo, en plural si es un listado. */
    @Input() title = '';
    /** Una línea. Obligatoria salvo que la vista sea trivial. */
    @Input() description = '';
    @Input() breadcrumb: DxcCrumb[] = [];
    /** Solo en vistas de detalle a las que se llega desde un listado. */
    @Input() showBack = false;

    @Output() back = new EventEmitter<void>();

    /** El menú «Más» solo existe en un teléfono. */
    readonly phone = dxcBelow('sm');
}
